import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  EmptyState,
  SectionHeader,
  StatCard,
} from "../../components/design-system/index.js";
import {
  getStressTest,
  type StressResponse,
  type StressScenarioKey,
} from "../../lib/api-modules/portfolio.js";
import { formatCentsCurrency } from "../../lib/util/money.js";
import { SuggestedPromptsTile } from "../../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../../lib/data/page-prompts.js";

/**
 * /portfolio/stress-test — Phase 11 Cleave 3.
 *
 * Applies historical (or custom) market shocks to the household's
 * current portfolio. Buckets are derived from each holding's asset
 * class via a ticker-name heuristic.
 */
export function PortfolioStressTestPage() {
  const [scenario, setScenario] = useState<StressScenarioKey>("gfc_2008");
  const { data, isLoading, error } = useQuery<StressResponse>({
    queryKey: ["portfolio", "stress-test", scenario],
    queryFn: () => getStressTest({ scenario }),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
      <SectionHeader
        title="Stress test"
        sub="Project the dollar impact of historical market shocks on your current portfolio."
        action={
          <Link
            to="/portfolio"
            className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-xs font-medium text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
          >
            ← Portfolio
          </Link>
        }
      />

      <Card>
        <div className="flex flex-wrap gap-2">
          {(
            [
              "gfc_2008",
              "covid_2020",
              "inflation_2022",
              "stagflation_70s",
            ] as StressScenarioKey[]
          ).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setScenario(k)}
              className={
                k === scenario
                  ? "rounded-full bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-contrast)]"
                  : "rounded-full border border-[var(--color-border-default)] px-3 py-1.5 text-xs text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
              }
            >
              {k.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </Card>

      {isLoading && <p className="text-sm text-[var(--color-muted)]">Loading…</p>}
      {error && (
        <Card>
          <p className="text-sm text-[var(--color-danger)]">
            {error instanceof Error ? error.message : "Failed"}
          </p>
        </Card>
      )}
      {data && data.before_cents === 0 && (
        <EmptyState
          title="No holdings on file"
          description="Add brokerage holdings (manually or via a sync integration) to run stress tests."
        />
      )}

      {data && data.before_cents > 0 && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              label="Before"
              value={formatCentsCurrency(data.before_cents)}
            />
            <StatCard
              label={`After ${data.scenario.label}`}
              value={formatCentsCurrency(data.after_cents)}
              sub={`Equity ${data.scenario.equity_delta_pct}% · Bond ${data.scenario.bond_delta_pct}%`}
            />
            <StatCard
              label="Delta"
              value={formatCentsCurrency(data.delta_cents)}
              sub={`${data.delta_pct.toFixed(2)}%`}
              trendDirection={data.delta_cents >= 0 ? "up" : "down"}
            />
          </div>
          <Card>
            <p className="mb-3 text-xs text-[var(--color-muted)]">{data.methodology}</p>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="py-2">Asset class</th>
                  <th className="py-2">Account type</th>
                  <th className="py-2 text-right">Before</th>
                  <th className="py-2 text-right">After</th>
                  <th className="py-2 text-right">Delta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border-subtle)]">
                {data.buckets.map((b, i) => (
                  <tr key={i}>
                    <td className="py-2 capitalize text-[var(--color-text)]">
                      {b.asset_class}
                    </td>
                    <td className="py-2 text-xs text-[var(--color-muted)]">
                      {b.label ?? "—"}
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                      {formatCentsCurrency(b.before_cents)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                      {formatCentsCurrency(b.after_cents)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                      {formatCentsCurrency(b.delta_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.portfolioStressTest} />
      </div>
    </div>
  );
}
